import React from 'react';
import { Badge } from '../ui/badge';
import { Check, Lock, ArrowRight, ExternalLink } from 'lucide-react';

const BookArchitecture = ({ series }) => {
  const accent = series?.accentColor || '#DC2626';
  const books = series?.books || [];

  // Status is sourced from the series seed (content.py) via /api/content/series/:slug.
  // Book 1 is on Amazon (special-cased); everything else comes from book.status.
  const getBookStatus = (book) => {
    if (book.number === 1) return 'amazon';
    if (book.status === 'available') return 'available';
    return 'coming-soon';
  };

  return (
    <section className="py-20 bg-slate-950 relative overflow-hidden" id="books">
      {/* Subtle grid bg */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="h-full w-full" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: accent }}>
            The 8-Book System
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            A structured path from <span style={{ color: accent }}>self-leadership</span> to <span style={{ color: accent }}>strategy</span>
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto">
            Each book builds on the last. Start where you are, progress at your pace.
          </p>
        </div>

        {/* Book grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-5 max-w-5xl mx-auto mb-10">
          {books.map((book) => {
            const status = getBookStatus(book);
            const isPublished = status === 'available' || status === 'amazon';

            return (
              <div
                key={book.number}
                className={`group relative rounded-xl overflow-hidden transition-all duration-300 ${
                  isPublished
                    ? 'hover:-translate-y-2 hover:shadow-xl hover:shadow-black/30'
                    : 'opacity-70 hover:opacity-90'
                }`}
                data-testid={`book-arch-${book.number}`}
              >
                {/* Cover image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  {book.coverImage ? (
                    <img
                      src={book.coverImage}
                      alt={`Book ${book.number}: ${book.title}`}
                      className={`w-full h-full object-cover transition-transform duration-500 ${
                        isPublished ? 'group-hover:scale-105' : 'grayscale-[30%]'
                      }`}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: book.color || '#333' }}
                    >
                      <span className="text-white/60 text-4xl font-bold">{book.number}</span>
                    </div>
                  )}

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  {/* Status badge */}
                  <div className="absolute top-2 left-2">
                    {status === 'available' && (
                      <Badge className="bg-emerald-500 text-white border-0 text-[10px] px-2 py-0.5 shadow">
                        <Check className="w-3 h-3 mr-0.5" /> NEW
                      </Badge>
                    )}
                    {status === 'amazon' && (
                      <Badge className="bg-white text-slate-800 border-0 text-[10px] px-2 py-0.5 shadow">
                        <ExternalLink className="w-3 h-3 mr-0.5" /> Amazon
                      </Badge>
                    )}
                    {status === 'coming-soon' && (
                      <Badge className="bg-slate-800/80 text-slate-300 border-0 text-[10px] px-2 py-0.5 backdrop-blur-sm">
                        <Lock className="w-3 h-3 mr-0.5" /> Coming
                      </Badge>
                    )}
                  </div>

                  {/* Book info overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-[10px] uppercase tracking-wider font-medium mb-0.5"
                       style={{ color: book.color || '#fff' }}>
                      Book {book.number} &middot; {book.theme}
                    </p>
                    <h3 className="text-sm font-bold text-white leading-tight">
                      {book.title}
                    </h3>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Journey progression bar */}
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your progression</span>
            <div className="flex-1 h-px bg-slate-800" />
          </div>
          <div className="flex items-center gap-1">
            {books.map((book, i) => {
              const status = getBookStatus(book);
              const isPublished = status === 'available' || status === 'amazon';
              return (
                <React.Fragment key={book.number}>
                  <div
                    className="flex-1 h-2 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: isPublished ? book.color : '#1e293b',
                      opacity: isPublished ? 1 : 0.4
                    }}
                    title={`Book ${book.number}: ${book.title}`}
                  />
                  {i < books.length - 1 && (
                    <ArrowRight className="w-3 h-3 text-slate-700 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-600">Lead Yourself</span>
            <span className="text-xs text-slate-600">Lead Strategically</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookArchitecture;
